import { AnimatedPageHeader } from '@components/index'
import { Table } from '@components/table'
import { PageRead } from '@components/table/types'
import styles from '@features/offers/components/OffersTable.module.scss'
import { OffersTableFilters } from '@features/offers/components/OffersTableFilters'
import { useDeleteOffer } from '@services/offers/mutations/useDeleteOffer'
import { useOffers } from '@services/offers/queries/useOffers'
import { ExternalLink, Trash2, User } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { OfferList, UserShort } from 'types/api'
import { BaseResponse } from 'types/utils'

import { Button } from '@/components/ui/button'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { cn } from '@/lib/utils'

const STATUS_TONE: Record<string, string> = {
  CREATED: 'bg-primary/15 text-primary',
  INTERESTED: 'bg-peach/30 text-warning',
  ACCEPTED: 'bg-primary/15 text-primary',
  RESERVED: 'bg-peach/30 text-warning',
  ONGOING: 'bg-primary/15 text-primary',
  NOTICE_PERIOD: 'bg-peach/30 text-warning',
  TERMINATED: 'bg-destructive/15 text-destructive',
  REJECTED: 'bg-destructive/15 text-destructive',
  CANCELED: 'bg-ink/[0.07] text-ink/60'
}

const Money = ({ value }: { value: string | null }) =>
  value == null ? (
    <>-</>
  ) : (
    <span className="font-semibold text-ink">
      {value} <span className="font-normal text-muted-foreground">€</span>
    </span>
  )

function UserPill({ user }: { user: UserShort }) {
  const name =
    user.first_name || user.last_name
      ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
      : user.email
  return (
    <Link
      to={`/admin/users/${user.id}`}
      onClick={(e) => e.stopPropagation()}
      title={user.email ?? ''}
      className="inline-flex w-fit items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary transition-colors hover:bg-primary/20"
    >
      <User className="h-3.5 w-3.5" />
      {name}
    </Link>
  )
}

// Offers that have not reached reservation (plus finished ones) may be
// deleted by an admin — mirrors the backend rule in delete_offer.
export const DELETABLE_OFFER_STATUSES = new Set([
  'CREATED',
  'INTERESTED',
  'ACCEPTED',
  'CANCELED',
  'REJECTED'
])

function OfferRowActions({ record }: { record: OfferList }) {
  const { t } = useTranslation('offers')
  const { confirm, dialog } = useConfirm()
  const deleteOffer = useDeleteOffer()

  if (!record.id || !DELETABLE_OFFER_STATUSES.has(record.status ?? '')) {
    return null
  }
  const id = record.id

  return (
    // Stop propagation so the action doesn't trigger row navigation.
    <div
      onClick={(e) => e.stopPropagation()}
      className="flex justify-center"
    >
      <button
        type="button"
        title={t('admin.actions.delete_offer')}
        disabled={deleteOffer.isPending}
        onClick={async () => {
          const confirmed = await confirm({
            title: t('admin.actions.confirm_delete_offer'),
            description: t('admin.actions.delete_offer_description'),
            destructive: true,
            confirmText: t('admin.form.yes'),
            cancelText: t('admin.form.no')
          })
          if (confirmed) deleteOffer.mutate({ id })
        }}
        className="flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent p-0 text-ink/40 transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
      >
        <Trash2 className="h-4 w-4" />
      </button>
      {dialog}
    </div>
  )
}

function isContractExpiringSoon(record: OfferList): boolean {
  if (!record.contract_end_date) return false
  const endDate = new Date(record.contract_end_date)
  const now = new Date()
  const threeMonths = new Date()
  threeMonths.setMonth(threeMonths.getMonth() + 3)
  return endDate >= now && endDate <= threeMonths
}

export function OffersTable(props: {
  user?: string
  flat?: string
  application?: string
}) {
  const { user, flat, application } = props
  const isEmbedded = !!(user || flat)
  const query = useOffers({ params: { user, flat } })
  const navigate = useNavigate()
  const { t } = useTranslation('offers')

  const columns = [
    {
      title: t('sections.property'),
      dataIndex: 'flat',
      render: (value: OfferList['flat'], record: OfferList) => {
        const locationParts = [
          value?.location_name,
          [record.address, record.address_number].filter(Boolean).join(' ')
        ].filter(Boolean)
        return (
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="font-medium text-ink">
                {value?.title || '-'}
              </span>
              {value?.id && (
                <Link
                  to={`/admin/flats/${value.id}`}
                  onClick={(e) => e.stopPropagation()}
                  title={t('navigation.show_flat', { ns: 'flat' })}
                  className="shrink-0 text-ink/35 transition-colors hover:text-primary"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </Link>
              )}
            </div>
            {locationParts.length > 0 && (
              <div className="mt-0.5 text-xs text-muted-foreground">
                {locationParts.join(' · ')}
              </div>
            )}
          </div>
        )
      }
    },
    {
      title: t('sections.user'),
      dataIndex: 'user',
      render: (value: UserShort, record: OfferList) => {
        if (!value?.email) return null
        return (
          <div className="flex flex-col items-start gap-1.5">
            <UserPill user={value} />
            {record.roommates?.map((rm) => (
              <UserPill key={rm.id} user={rm} />
            ))}
          </div>
        )
      }
    },
    {
      title: t('sections.rent'),
      dataIndex: 'monthly_rent_sum',
      render: (value: string | null) => <Money value={value} />
    },
    {
      title: t('sections.deposit'),
      dataIndex: 'deposit_sum',
      sorter: true,
      render: (value: string | null) => <Money value={value} />
    },
    {
      title: t('sections.status'),
      dataIndex: 'status',
      sorter: true,
      render: (value: OfferList['status']) => (
        <span
          className={cn(
            'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold',
            STATUS_TONE[value || ''] ?? 'bg-ink/[0.07] text-ink/60'
          )}
        >
          {t(`statuses.${value}`)}
        </span>
      )
    },
    {
      title: t('sections.contract_start'),
      dataIndex: 'contract_start_date',
      render: (value: OfferList['contract_start_date']) =>
        value ? new Date(value).toLocaleDateString('sk-SK') : '-',
      sorter: true
    },
    {
      title: t('sections.contract_end'),
      dataIndex: 'contract_end_date',
      render: (value: OfferList['contract_end_date']) =>
        value ? new Date(value).toLocaleDateString('sk-SK') : '-',
      sorter: true
    },
    {
      title: '',
      key: 'actions',
      width: 56,
      render: (_: unknown, record: OfferList) => (
        <OfferRowActions record={record} />
      )
    }
    // The client is already known when scoped to a user (e.g. application detail).
  ].filter((col) => !(user && col.dataIndex === 'user'))

  const handleClickCreate = () => {
    let url = `/admin/offers/create?`
    if (application) url += `applications=${application}&`
    if (flat) url += `flat=${flat}&`
    navigate(url)
  }

  if (isEmbedded) {
    return (
      <Table<BaseResponse<OfferList>>
        query={query}
        columns={columns}
        id="offers"
        hasCreateButton
        onCreate={handleClickCreate}
        rowClassName={(record: OfferList) =>
          isContractExpiringSoon(record) ? (styles.expiringRow ?? '') : ''
        }
      />
    )
  }

  return (
    <div className="space-y-6">
      <AnimatedPageHeader title={t('title')} />
      <OffersTableFilters
        actionBar={
          <Button onClick={handleClickCreate}>{t('filters.createOffer')}</Button>
        }
      />
      <Table<PageRead<OfferList>>
        query={query}
        columns={columns}
        id="offers"
        hasSearch={false}
        rowClassName={(record: OfferList) =>
          isContractExpiringSoon(record) ? (styles.expiringRow ?? '') : ''
        }
      />
    </div>
  )
}
